// WHICH copy link the panel puts in front of the owner. No React, no fetch
// (lib/ convention). One definition, deliberately: "the current link" is named in
// the action row, the visible URL, the copy button, the revoke button and the row
// glyph, and a second notion of it would let those five disagree.
//
// THE RULE: the newest LIVE link that has a usable URL.
//
// Newest is free — `listCopyLinks` orders `created_at DESC` (copyLinks.js) and both
// the hook and the panel keep that order verbatim — so `find` returns the newest
// match without this module sorting anything. Do not add a sort here; it would be a
// second copy of the server's ordering rule, free to disagree with it silently.
//
// LIVE IS THE GUARD, AND IT LIVES HERE NOW. The server hands an owner the URL for
// each of their rows, and it will happily hand back one for a row revoked in
// another tab or expired since the last read. Displaying that is worse than
// displaying nothing: the owner passes on a link that 410s and has no reason to
// suspect it. The panel keeps its own per-row check for the glyph — a component
// validating its own props — but the decision of WHICH link is current is made
// exactly once, here, where it is cheap to test exhaustively.
//
// USABLE means `isCapabilityUrl`: a non-empty string with something after a `#`.
// Two reasons it is not merely "truthy". Rows minted before the `secret` column
// carry `url: null` forever, and a URL with no fragment carries no secret — it
// would render as a link and redeem as nothing. Reusing the stash's predicate
// rather than restating it keeps the two from drifting; the capsule's rule about
// hand-kept duplicates is easier to honour by not creating one.

import { isCapabilityUrl } from "./mintedStash.js";

/**
 * The link whose URL the panel should show, or null.
 *
 * @param {Array<object>|unknown} links  `GET /api/copy-links` rows, server order
 * @param {{copyId?: string|null, url?: string|null}|null} [stash]  this tab's
 *   `useMintedLinkStash` holding, used ONLY as a fallback — see below
 * @returns {{copyId: string, url: string, createdAt: unknown}|null}
 *   `createdAt` is passed through VERBATIM, exactly as the wire sent it — the
 *   handler does not normalise it (a non-finite date would throw RangeError), so
 *   `null` is reachable and rendering is the caller's problem, not this module's.
 */
export function selectCurrentLink(links, stash = null) {
  const rows = Array.isArray(links) ? links : [];

  // The server's answer wins whenever it has one. It is authoritative, it survives
  // a reload and reaches every device, and it is per-ROW — where the stash is
  // per-forecast and holds only this tab's most recent mint. Preferring the stash
  // could pin the display to a link the owner has since superseded.
  const served = rows.find((l) => l?.state === "live" && isCapabilityUrl(l?.url));
  if (served) {
    return { copyId: served.copyId, url: served.url, createdAt: served.createdAt ?? null };
  }

  // FALLBACK, and it is narrower than it looks. It fires only when no live row has
  // a served URL at all — a deployment with `COPY_LINK_SECRET_KEY` unset, where
  // mint still returns a URL but the list cannot. Even then the stashed link must
  // still be present and LIVE in the list, so a stale stash cannot resurrect a
  // revoked capability or attach to a row that is not on screen.
  const id = stash && typeof stash.copyId === "string" ? stash.copyId : null;
  if (!id || !isCapabilityUrl(stash?.url)) return null;
  const row = rows.find((l) => l?.copyId === id && l?.state === "live");
  // The date comes from the ROW even on this path: the stash holds a URL, not a
  // record, and the list is the authority on when the link was made.
  return row ? { copyId: id, url: stash.url, createdAt: row.createdAt ?? null } : null;
}
